import { useEffect } from 'react';

type Options = {
  refetch: () => void;
  refetchOnVisibility?: boolean;
  refetchOnBFCacheRestore?: boolean;
};

/**
 * Hook to ensure fresh data is fetched when a page is restored from the back-forward cache (bfcache) or when the tab becomes visible again.
 *
 * @param refetch - A function that triggers the data re-fetch.
 * @param refetchOnVisibility - If true, refetches when the tab becomes visible (default: true).
 * @param refetchOnBFCacheRestore - If true, refetches when the page is restored from bfcache (default: true).
 */
export function useEnsureFreshData({
  refetch,
  refetchOnVisibility = true,
  refetchOnBFCacheRestore = true,
}: Options) {
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && refetchOnVisibility) {
        refetch();
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted && refetchOnBFCacheRestore) {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [refetch, refetchOnVisibility, refetchOnBFCacheRestore]);
}
